import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {Guid} from 'guid-typescript';
import {takeUntil} from 'rxjs/operators';
import {
  QuickSelectionModelId,
  SelectionEventModel,
  TreeItemNode,
  QuickSelectionModel
} from '@shared/models/tree-selector.models';
import {SnackBarService} from '@core/services/snack-bar.service';
import {MessageBarActions, PushMessageBarNotification} from '@shared/store/actions/message-bar.action';
import {ParticleState} from '@shared/store/reducers/particle.reduer';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {hasFeature} from '@shared/store/selectors/security.selector';
import {TransactionFeatures} from '../../transaction-features';
import {EntitySetting} from '../../../models/entity-setting.model';
import {PaymentStreamSetting} from '../../../models/payment-stream-setting.model';
import {ChangeReportEntity} from '../../../models/change-report-entity.model';
import {PaymentStream} from '../../../models/payment-stream.model';
import {TransactionState} from '../../store/reducers/transaction.reducer';
import {EntityChartState} from '../../../entity-chart/store/reducers/entity-chart.reducer';
import {
  getPayingEntities, getReceivingEntities, getRefreshSelectionCounter, getStreams, getTransactions
} from '../../store/selectors/transaction.selectors';
import {getEntityCount} from '../../../entity-chart/store/selectors/entity-chart.selector';
import {
  CreateTransactionsBetweenMyCountriesAction, DeleteAllTransactionsAction, FetchAllPayingEntitiesAction,
  FetchReceivingEntitiesAction, GetReceivingEntitiesStreamsAction, GetStreamsAction,
  ResetTransactionsFromProjectSettingsAction, TransactionAddAction, TransactionDeleteAction
} from '../../store/actions/transaction.actions';
import {GetEntityCountAction} from '../../../entity-chart/store/actions/entity-chart.actions';
import {ChangeReportTransaction} from '../../../models/change-report-transaction.model';
import {
  CreateTransactionsBetweenMyCountriesCommand,
  DeleteAllTransactionCommand,
  ResetTransactionsFromProjectSettingsCommand,
  TransactionAddCommand,
  TransactionDeleteCommand
} from '../../commands/transaction.commands';
import {CombineRoute} from '@shared/constants/RelativePath';
import {Router} from '@angular/router';
import {ISmartGridDataState} from '@shared/store/reducers/smart-grid.reducers';
import {getSmartGridItems} from '@shared/store/selectors/smart-grid.selectors';
import {InvalidateSmartGridAction} from '@shared/store/actions/smart-grid.actions';
import {SmartGridService} from '@core/services/smart-grid.service';

@Component({
    standalone: false,
    selector: 'lib-app-transaction-content',
  templateUrl: './transaction-content.component.html',
  styleUrls: ['./transaction-content.component.scss']
})
export class TransactionContentComponent implements OnInit, OnDestroy {

  // transaction footprint input start
  sourceA$: BehaviorSubject<EntitySetting[]> = new BehaviorSubject([]);
  sourceB$: BehaviorSubject<EntitySetting[]> = new BehaviorSubject([]);
  streamSelectorData$: BehaviorSubject<TreeItemNode[]> = new BehaviorSubject([]);
  streamQuickSelections$: Observable<QuickSelectionModel[]>;
  resetSelection$ = new Subject<any>();
  selectedSources$ = new Subject<any>();
  // transaction footprint input end

  sourceStream: PaymentStreamSetting[];
  private isReveresed = false;
  private payingEntity: ChangeReportEntity;
  private receivingEntity: ChangeReportEntity;
  private stream: PaymentStream;
  selectedIndex = 0;
  unSubscribe$ = new Subject<void>();

  hasFeature$: Observable<any>;
  features: any;
  transactionFeatures = TransactionFeatures;
  notificationId = 'transactionMessage';
  sourceType: string = 'Dac6-Transaction';
  public totalTransaction: number;
  public entityCount: number;

  constructor(private store: Store<TransactionState>,
              private entityStore: Store<EntityChartState>,
              private securityStore: Store<SecurityState>,
              private snackBarService: SnackBarService,
              private particleStore: Store<ParticleState>,
              private router: Router,
              private smartGridStore: Store<ISmartGridDataState>,
              private smartGridService: SmartGridService) {
    this.streamQuickSelections$ = of([{
      name: 'All streams', id: QuickSelectionModelId.ALL
    }, {
      name: 'None', id: QuickSelectionModelId.NONE
    }]);
    this.totalTransaction = 0;
    this.entityCount = 0;
  }

  ngOnInit() {
    this.smartGridService.initDataProvider(this.sourceType);
    this.particleStore.dispatch(new PushMessageBarNotification({
      'Id': 'transactionMessage',
      'IsClosed': false,
      'Message': 'The assumption is that all countries added to the transaction tab have cross border transactions with each other.' + ' To limit the type of transactions please pick your streams from stream footprint',
      'Links': [],
      'ShowCloseButton': true
    }));
    this.hasFeature$ = this.securityStore.pipe(select(hasFeature));
    this.hasFeature$.subscribe(features => {
      this.features = features;

    });
    this.initTransactionSettings();
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  initTransactionSettings() {
    this.getAllPayingEntities();
    this.getReceivingEntities();
    this.getStreams();
    this.subscribeToObservables();
    this.getEntityCount();
  }

  private subscribeToObservables() {
    this.store.select(getReceivingEntities).pipe(takeUntil(this.unSubscribe$)).subscribe(data => {
      this.sourceB$.next(data);
    });
    this.store.select(getPayingEntities).pipe(takeUntil(this.unSubscribe$)).subscribe(data => {
      this.sourceA$.next(data);
    });
    this.store.select(getStreams).pipe(takeUntil(this.unSubscribe$)).subscribe(data => {
      this.sourceStream = data;
      const streamData = this.buildStreamSelectorTree(this.sourceStream);
      this.streamSelectorData$.next(streamData);
    });
    this.store.select(getRefreshSelectionCounter).pipe(takeUntil(this.unSubscribe$)).subscribe(count => {
      if (count > 0) {
        this.resetSelection$.next({resetPaying: true, resetReceiving: true});
      }
    });

    this.entityStore.select(getEntityCount).pipe(takeUntil(this.unSubscribe$)).subscribe(count => {
      this.entityCount = count;
    });

    /*this.store.select(getTransactions).pipe(takeUntil(this.unSubscribe$)).subscribe((transactions) => {
      this.totalTransaction = transactions.length;
    });*/
    this.smartGridStore.pipe(select(getSmartGridItems, this.sourceType), takeUntil(this.unSubscribe$)).subscribe(item=>{
      if(item){
        this.totalTransaction = item.length;
      }
    })
  }

  private getReceivingEntities() {
    this.store.dispatch(new FetchReceivingEntitiesAction(this.payingEntity ? this.payingEntity.id : null));
  }

  private getAllPayingEntities() {
    this.store.dispatch(new FetchAllPayingEntitiesAction());
  }

  private getStreams() {
    this.store.dispatch(new GetStreamsAction(this.payingEntity ? this.payingEntity.id : null, this.receivingEntity ? this.receivingEntity.id : null));
  }

  private getEntityCount() {
    this.store.dispatch(new GetEntityCountAction());
  }

  navigateToTransactionsImport() {
    this.router.navigate(CombineRoute.TransactionsImportRoute);
  }

  buildStreamSelectorTree(streamSettings: PaymentStreamSetting[]) {
    const data: TreeItemNode[] = [];
    streamSettings.forEach(settings => {
      const node = new TreeItemNode();
      node.item = {...settings};
      node.item.title = settings.stream.title;
      data.push(node);
    });
    return data;
  }

  // transaction footprint event start
  onCountryClick(event: { settings: EntitySetting, isPaying: boolean }) {
    if (event.isPaying) {
      this.resetSelection$.next({resetPaying: false, resetReceiving: true});
      this.payingEntity = event.settings.entity;
      this.receivingEntity = null;
      this.getReceivingEntities();
      this.getStreams();
    } else {
      this.receivingEntity = event.settings.entity;
      this.getStreams();
    }
  }

  onStreamClick(event: SelectionEventModel) {
    if (event.currentTreeNode) {
      this.stream = event.currentTreeNode.item.stream;
      const transactions = [new ChangeReportTransaction({
        payingEntity: this.payingEntity, receivingEntity: this.receivingEntity, stream: this.stream
      })];
      if (event.currentTreeNode.item.isChecked) {
        const command = new TransactionAddCommand(transactions);
        this.store.dispatch(new TransactionAddAction(command, this.sourceType));
      } else {
        const command = new TransactionDeleteCommand(transactions);
        this.store.dispatch(new TransactionDeleteAction(command, this.sourceType));
      }
    } else if (event.selectedTreeNodes) {
      const transactions = event.selectedTreeNodes.map(n => {
        return new ChangeReportTransaction({
          payingEntity: this.payingEntity, receivingEntity: this.receivingEntity, stream: n.item.stream
        });
      });
      if (event.isCurrentTreeNodeChecked) {
        const command = new TransactionAddCommand(transactions);
        this.store.dispatch(new TransactionAddAction(command, this.sourceType));
      } else {
        const command = new TransactionDeleteCommand(transactions);
        this.store.dispatch(new TransactionDeleteAction(command, this.sourceType));
      }

    }
    //this.smartGridStore.dispatch(new InvalidateSmartGridAction(this.sourceType, true, true, true));
  }

  resetTransactionFromProjectSettings() {
    const command = new ResetTransactionsFromProjectSettingsCommand();
    this.store.dispatch(new ResetTransactionsFromProjectSettingsAction(command, this.sourceType));
    //this.smartGridStore.dispatch(new InvalidateSmartGridAction(this.sourceType, true, true, true))
  }

  // transaction footprint event end

  tabChanged(tab) {
    this.selectedIndex = tab.index;
  }

  setSelectedSources(event: any) {
    if (this.features(this.transactionFeatures.CRTransactionTopicEdit)) {
      this.payingEntity = event.selectedRows[0].dataItem.cells.PayingEntity.value;
      this.receivingEntity = event.selectedRows[0].dataItem.cells.ReceivingEntity.value;
      this.store.dispatch(new GetReceivingEntitiesStreamsAction(event.selectedRows[0].dataItem.cells.PayingEntity.value.id, event.selectedRows[0].dataItem.cells.ReceivingEntity.value.id));
      this.selectedSources$.next({
        sourceASelectedItem: event.selectedRows[0].dataItem.cells.PayingEntity.value.id,
        sourceBSelectedItem: event.selectedRows[0].dataItem.cells.ReceivingEntity.value.id
      });
      this.selectedIndex = 0;
    } else {
      this.snackBarService.showError('Permission denied');
    }
  }

  createTransactionsBetweenMyCountries() {
    const command = new CreateTransactionsBetweenMyCountriesCommand();
    this.store.dispatch(new CreateTransactionsBetweenMyCountriesAction(command, this.sourceType));
    //this.smartGridStore.dispatch(new InvalidateSmartGridAction(this.sourceType, true, true, true))
  }

  deleteAllTransaction() {
    const command = new DeleteAllTransactionCommand();
    this.store.dispatch(new DeleteAllTransactionsAction(command, this.sourceType));
    //this.smartGridStore.dispatch(new InvalidateSmartGridAction(this.sourceType, true, true, true))
  }
}
